import type { InitFormRequest } from 'apiConnector/form/static';
import { staticFormApiConnectorLegacy } from 'apiConnector/form/static';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { defaultFormReceivedThunk } from 'state/static/staticForm.thunks';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

export function fetchDefaultStaticFormEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      selectors: { getUserPreferencesCurrency, isUserPreferencesPrivateMode, getUserPreferencesXMargin },
    },
  ) => {
    const productSelectedTabId$: Observable<InitFormRequest> = action$.pipe(
      ofType('WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED'),
      filter(action => action.workspaceType === 'static'),
      withLatestFrom(state$),
      map(([action, state]) => ({
        tabId: action.tabId,
        tradeDate: getTodayStringInLocalTimezone(),
        productType: action.productVariation.productType,
        productSubType: action.productVariation.productSubType,
        wrapper: action.productVariation.wrapper,
        isPrivateMode: isUserPreferencesPrivateMode(state),
        currency: getUserPreferencesCurrency(state),
        selectedOnBehalfClient: action.client ?? undefined,
        xMargin: getUserPreferencesXMargin(state),
      })),
    );

    const resetTabId$: Observable<InitFormRequest> = action$.pipe(
      ofType('WORKSPACE_RESET_TAB_REQUESTED'),
      filter(action => action.workspace === 'static'),
      withLatestFrom(state$),
      map(([{ tabId, productType, productSubType, wrapper, selectedOnBehalfClient }, state]) => ({
        tabId,
        tradeDate: getTodayStringInLocalTimezone(),
        productType,
        productSubType,
        wrapper,
        selectedOnBehalfClient,
        isPrivateMode: isUserPreferencesPrivateMode(state),
        currency: getUserPreferencesCurrency(state),
        xMargin: getUserPreferencesXMargin(state),
      })),
    );

    const tabId$ = merge(productSelectedTabId$, resetTabId$);

    return tabId$.pipe(
      mergeMap(request => {
        const { tabId } = request;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const apiCall = staticFormApiConnectorLegacy(oldHttpClient).initForm(request);

        return apiCall.pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              productView,
              blocksMetadata,
              fieldsMetadata,
              values,
              version,
              negotiationId,
              actions,
              quoteQuery,
              errors,
              mustForceAdvancedMode,
              sendWholeFormData,
              allowedRequests,
            }) =>
              of(
                defaultFormReceivedThunk(
                  tabId,
                  productView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                  sendWholeFormData,
                ),
              ),
          ),
          catchError(error =>
            of(
              commonFormActions.defaultFormFailed(
                'static',
                tabId,
                error.message || 'default form failed',
                error.traceId,
              ),
            ),
          ),
          timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('static', tabId)), appScheduler),
        );
      }),
    );
  };
}
